import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  AlertCircle,
  Bell,
  Loader2,
  ScrollText,
} from 'lucide-react'
import { AnalystNavigation } from '../components/Navigation'
import { InvestigationDetail } from '../components/InvestigationDetail'
import { SeverityBadge } from '../components/SeverityBadge'
import { StatusBadge } from '../components/StatusBadge'
import { formatDate, timeAgo } from '../lib/utils'
import { getCaseLifecycle } from '../lib/api'
import type { CaseLifecycle } from '../types'

/**
 * Read-only case detail, rendered entirely from persisted data via
 * /cases/{id}/lifecycle. The old version of this page rendered a fabricated
 * timeline, fabricated signals, a fabricated household panel, and "Manual
 * Action" buttons wired to an endpoint that does not exist — all deleted
 * (§7 honest alerting; TASKS/analyst-escalation-view.md). This slice has no
 * mutations and no action controls; response stays disarmed.
 */
export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [lifecycle, setLifecycle] = useState<CaseLifecycle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    getCaseLifecycle(id)
      .then((data) => {
        if (!cancelled) setLifecycle(data)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this case from the backend.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-brass animate-spin" />
          <p className="text-sm text-gray-500">Loading case...</p>
        </div>
      </div>
    )
  }

  if (error || !lifecycle) {
    return (
      <div className="min-h-screen bg-cream">
        <AnalystNavigation />
        <main className="max-w-3xl mx-auto px-6 py-10">
          <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            {error ?? 'Case not found.'} Nothing is shown because nothing could
            be loaded — this page never invents case data.
          </div>
          <button
            type="button"
            onClick={() => navigate('/analyst')}
            className="tap-min mt-4 flex items-center gap-1 text-sm text-gray-500 hover:text-navy transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to queue
          </button>
        </main>
      </div>
    )
  }

  const c = lifecycle.case

  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />

      <main className="max-w-5xl mx-auto px-6 py-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <button
            type="button"
            onClick={() => navigate('/analyst')}
            className="tap-min flex items-center gap-1 text-sm text-gray-500 hover:text-navy mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to queue
          </button>

          <div className="mb-5">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="font-mono text-sm text-gray-500">{c.case_number}</span>
              <SeverityBadge severity={c.severity} size="sm" />
              <StatusBadge status={c.status} size="sm" />
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Tier {c.autonomy_tier}
              </span>
              <span className="ml-auto text-xs text-gray-400">
                {c.created_at ? `${formatDate(c.created_at)} · ${timeAgo(c.created_at)}` : ''}
              </span>
            </div>
            <h1 className="text-2xl font-serif font-bold text-navy">{c.title}</h1>
            {c.description && <p className="mt-1 text-sm text-gray-600">{c.description}</p>}
            {c.principal_id && (
              <p className="text-xs text-gray-400 mt-0.5">
                principal <code>{c.principal_id}</code>
              </p>
            )}
          </div>

          <div className="space-y-5">
            {/* Concierge narrative */}
            <section className="bg-white rounded-xl border border-gray-200 p-5" aria-label="Concierge narrative">
              <h2 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                <ScrollText className="w-4 h-4 text-brass" aria-hidden="true" />
                Concierge narrative
              </h2>
              {c.narrative ? (
                <p className="text-sm leading-6 text-navy whitespace-pre-wrap font-serif">
                  {c.narrative}
                </p>
              ) : (
                <p className="text-xs text-gray-500 italic">
                  No narrative was generated for this case.
                </p>
              )}
            </section>

            {/* Investigation */}
            <section className="bg-white rounded-xl border border-gray-200 p-5" aria-label="Investigation">
              <h2 className="text-sm font-semibold text-navy mb-3">Investigation</h2>
              <InvestigationDetail investigation={lifecycle.investigation} />
            </section>

            {/* Signals */}
            <section className="bg-white rounded-xl border border-gray-200 p-5" aria-label="Signals">
              <h2 className="text-sm font-semibold text-navy mb-3">
                Triggering signals ({lifecycle.signals.length})
              </h2>
              {lifecycle.signals.length === 0 ? (
                <p className="text-xs text-gray-500 italic">
                  No signals are linked to this case in the database.
                </p>
              ) : (
                <div className="space-y-2">
                  {lifecycle.signals.map((s) => (
                    <details key={s.id} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                      <summary className="cursor-pointer text-sm font-medium text-navy">
                        <code>{s.signal_type}</code>
                        <span className="ml-2 text-xs text-gray-500">
                          severity {s.severity ?? 'unassessed'} · confidence {s.confidence == null ? 'unassessed' : `${Math.round(s.confidence * 100)}%`}
                          {s.occurred_at ? ` · ${formatDate(s.occurred_at)}` : ''}
                        </span>
                      </summary>
                      <pre className="mt-2 text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                        {JSON.stringify(s.context, null, 1)}
                      </pre>
                    </details>
                  ))}
                </div>
              )}
            </section>

            {/* Alerts */}
            <section className="bg-white rounded-xl border border-gray-200 p-5" aria-label="Alerts">
              <h2 className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-brass" aria-hidden="true" />
                Alerts raised ({lifecycle.alerts.length})
              </h2>
              {lifecycle.alerts.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No alerts recorded for this case.</p>
              ) : (
                <div className="space-y-2">
                  {lifecycle.alerts.map((a, i) => (
                    <div key={i} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-navy flex-1">{a.headline}</p>
                        <StatusBadge status={a.status} size="sm" />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        via {a.channel}
                        {a.created_at ? ` · ${formatDate(a.created_at)}` : ''}
                      </p>
                      {a.action_taken && (
                        <p className="text-xs text-gray-600 mt-1">{a.action_taken}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Audit trail */}
            {lifecycle.audit_trail.length > 0 && (
              <section className="bg-white rounded-xl border border-gray-200 p-5" aria-label="Audit trail">
                <h2 className="text-sm font-semibold text-navy mb-3">Audit trail</h2>
                <div className="divide-y divide-gray-100">
                  {lifecycle.audit_trail.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 text-xs">
                      <code className="text-navy">{entry.action_type}</code>
                      <span className="text-gray-500">{entry.actor_type}</span>
                      <span className="ml-auto text-gray-400">
                        {entry.created_at ? formatDate(entry.created_at) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  )
}
